import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import EnhancedTableToolbar from "./SortingTableToolbar";
import { useEffect, useState } from "react";
import EnhancedTableHead, { getComparator, headCells, Order } from "./SortingTableHead";
import { useDispatch, useSelector } from "react-redux";
import { EXAM_OVERVIEW_TYPES } from "../../ExamOverview/ExamOverviewReducer";
import { RootState } from "../../..";
import CircularProgress from "@mui/material/CircularProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { ExamOverviewRow } from "../../../interfaces/examInterfaces";
import { TableRowItem } from "./TableRowItem";
import { TableRowFolder } from "./TableRowFolder";
import { handleOpenDialog } from "../../ExamBuilder/Functions/FunctionsMain";
import { DIALOGS } from "../../Dialogs/dialogReducer";

interface SortingTableProps {
  paths: { [key: string]: ExamOverviewRow[] } | null;
  rows: ExamOverviewRow[];
  selected: ExamOverviewRow | null;
  validGrades: number[] | null;
  setSelected: React.Dispatch<React.SetStateAction<ExamOverviewRow | null>>;
  openExam: (examId: string) => void;
  openDialogNewExam: (dialogName: string) => void;
}

export const SortingTable = ({
  paths,
  rows: rowData,
  selected,
  validGrades,
  setSelected,
  openExam,
  openDialogNewExam,
}: SortingTableProps) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ExamOverviewRow>("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [rows, setRows] = useState<ExamOverviewRow[]>([]);
  const pathsLoading = useSelector((state: RootState) => state.examOverview.pathsLoading);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setRows(rowData);
  }, [rowData]);

  useEffect(() => {
    setSelectedRow(selected);
  }, [location]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ExamOverviewRow) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClickSelectRow = (event: React.MouseEvent<unknown>, selectedRow: ExamOverviewRow) => {
    setSelectedRow(selectedRow);
    setMenuButton(selectedRow);
  };

  function setSelectedRow(selectedRow: ExamOverviewRow | null) {
    let newSelected: ExamOverviewRow | null = null;

    if (selectedRow?.id === selected?.id) {
      newSelected = null;
      dispatch({ type: EXAM_OVERVIEW_TYPES.DESELECT_ROW, payload: null });
    } else {
      newSelected = selectedRow;
      dispatch({ type: EXAM_OVERVIEW_TYPES.SELECT_ROW, payload: { newSelected } });
    }
    setSelected(newSelected);
  }

  function setMenuButton(selectedRow: ExamOverviewRow) {
    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, showOptions: !row.showOptions } : { ...row, showOptions: false }
    );
    setRows(updatedRows);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function openFolder(folderName: string) {
    const pathString = decodeURI(location.pathname.replace("/exam-overview", "")) || "/";

    if (folderName) {
      const current = pathString + (pathString === "/" ? "" : "/");
      navigate("/exam-overview" + current + encodeURI(folderName));
    }
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  
  const visibleRows = React.useMemo(
    () => [...rows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, order, orderBy, page, rowsPerPage]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar paths={paths} rowSelected={selected} openExam={openExam} validGrades={validGrades} />
        <TableContainer>
          <Table aria-labelledby="tableTitle">
            <colgroup>
              <col style={{ width: "4%" }} />
              <col style={{ width: "46%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "26%" }} />
            </colgroup>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected?.id.includes(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                let accessExpired: Boolean | undefined = undefined;
                if (row.grade) {
                  accessExpired = !validGrades?.includes(row.grade);
                }

                return (
                  <React.Fragment key={row.id}>
                    {row.isDirectory ? (
                      <TableRowFolder
                        handleClickSelectRow={handleClickSelectRow}
                        row={row}
                        isItemSelected={isItemSelected}
                        openFolder={openFolder}
                        labelId={labelId}
                        selected={selected}
                      />
                    ) : (
                      <TableRowItem
                        handleClickSelectRow={handleClickSelectRow}
                        row={row}
                        isItemSelected={isItemSelected}
                        openItem={openExam}
                        labelId={labelId}
                        selected={selected}
                        accessExpired={accessExpired}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={headCells.length + 1} />
                </TableRow>
              )}
              {rows.length === 0 && !pathsLoading && (
                <TableRow onClick={(event) => openDialogNewExam(DIALOGS.ADD_EXAM_DIALOG)}>
                  <TableCell colSpan={headCells.length + 1} sx={{ textAlign: "center" }}>
                    Keine Prüfung? Die Kids jubeln.
                  </TableCell>
                </TableRow>
              )}
              {rows.length === 0 && pathsLoading && (
                <TableRow>
                  <TableCell colSpan={headCells.length + 1} sx={{ textAlign: "center" }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
